import React, { forwardRef, useRef, useImperativeHandle } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { normalizeDateOnly } from '../../../utils/dateOnlyUtils';
import './AppDateInput.css';

/** Kept for callers that imported the old portal id (no longer used). */
export const APP_DATE_INPUT_PORTAL_ID = 'app-date-input-portal';

export const isoToLocalDate = (iso) => {
  const ymd = normalizeDateOnly(iso);
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map((part) => Number.parseInt(part, 10));
  return new Date(y, m - 1, d);
};

export const localDateToIso = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * App-wide date input — same native calendar as Patient Overview → Custom.
 * Value / min / max are YYYY-MM-DD; onChange receives a YYYY-MM-DD string.
 */
const AppDateInput = forwardRef(function AppDateInput(
  {
    value = '',
    onChange,
    min,
    max,
    className = '',
    wrapperClassName = '',
    disabled = false,
    id,
    name,
    placeholder = 'mm/dd/yyyy',
    autoComplete = 'off',
    isClearable = false,
    showCalendarIcon = false,
    // Native type="date" already constrains entry; keep prop for API compat
    readOnly: _readOnly = false,
    // Legacy react-datepicker props — accepted and ignored
    popperPlacement: _popperPlacement,
    popperProps: _popperProps,
    ...rest
  },
  ref
) {
  const inputRef = useRef(null);

  const openPicker = (event) => {
    if (disabled) return;
    event?.preventDefault?.();
    const el = inputRef.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === 'function') {
        el.showPicker();
        return;
      }
    } catch (_) {
      /* showPicker can throw if not triggered by user gesture */
    }
    el.focus();
    el.click();
  };

  // Keep setOpen() for call sites that opened the old react-datepicker via ref.
  useImperativeHandle(ref, () => ({
    ...(inputRef.current || {}),
    focus: (...args) => inputRef.current?.focus?.(...args),
    blur: (...args) => inputRef.current?.blur?.(...args),
    click: (...args) => inputRef.current?.click?.(...args),
    showPicker: (...args) => {
      try {
        return inputRef.current?.showPicker?.(...args);
      } catch (_) {
        openPicker();
      }
    },
    setOpen: (open) => {
      if (open) openPicker();
    },
  }));

  const handleChange = (event) => {
    if (typeof onChange !== 'function') return;
    onChange(event.target.value || '');
  };

  const input = (
    <input
      {...rest}
      ref={inputRef}
      type="date"
      id={id}
      name={name}
      className={className}
      value={normalizeDateOnly(value) || ''}
      min={min || undefined}
      max={max || undefined}
      disabled={disabled}
      autoComplete={autoComplete}
      placeholder={placeholder}
      title={placeholder}
      onChange={handleChange}
      onClick={openPicker}
    />
  );

  if (!showCalendarIcon && !isClearable) {
    if (!wrapperClassName) return input;
    return <div className={`app-date-input-wrapper ${wrapperClassName}`.trim()}>{input}</div>;
  }

  return (
    <div
      className={`app-date-input-wrapper ${showCalendarIcon ? 'app-date-input-with-icon' : ''} ${wrapperClassName}`.trim()}
    >
      {input}
      {isClearable && !disabled && value ? (
        <button
          type="button"
          className="app-date-input-clear-btn"
          aria-label="Clear date"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (typeof onChange === 'function') onChange('');
          }}
        >
          ×
        </button>
      ) : null}
      {showCalendarIcon ? (
        <button
          type="button"
          className="app-date-input-calendar-btn"
          aria-label="Open calendar"
          disabled={disabled}
          onClick={openPicker}
          tabIndex={-1}
        >
          <FontAwesomeIcon icon={faCalendarAlt} className="app-date-input-calendar-btn-icon" />
        </button>
      ) : null}
    </div>
  );
});

export default AppDateInput;
